import ExcelJS from 'exceljs';
import {
  computeEBStats, expectedBehaviourText, qualityBucket, detectLang,
  QUALITY_LABELS,
  type QualityBucket, type Lang,
} from './expected-behaviour';
import {
  checklistText, parseChecklist, checklistBucket,
  CHECKLIST_LABELS,
  type ChecklistBucket,
} from './checklist';
import type { Issue } from '../types/issue';

// Excel-Export der Expected-Behaviour-Qualitätsübersicht.
// Vier Sheets: Übersicht (KPIs + Ampeln) · Coverage (Team/Type) · Tickets
// (Detail mit JIRA-Links) · Legende & Info.

export type EBExportOptions = {
  jiraBaseUrl: string;
  team: string;                 // '' = alle Teams
  scope: 'open' | 'closed' | 'all';
};

const COLORS = {
  headerBg:      'FF1E3A8A',
  headerFg:      'FFFFFFFF',
  subHeaderBg:   'FFE0E7FF',
  subHeaderFg:   'FF1E3A8A',
  metaBg:        'FFF3F4F6',
  metaFg:        'FF374151',
  columnBg:      'FF334155',
  columnFg:      'FFFFFFFF',
  zebraBg:       'FFF8FAFC',
  border:        'FFCBD5E1',
  linkFg:        'FF1D4ED8',
  // Coverage-Ampel: hoher Wert = grün, niedriger = rot
  covHigh:       'FF10B981',
  covMed:        'FFF59E0B',
  covLow:        'FFF97316',
  covVeryLow:    'FFDC2626',
  // Bucket-Farben (aus expected-behaviour.ts, in ARGB)
  bucketEmpty:   'FF9E9E9E',
  bucketStub:    'FFC62828',
  bucketShort:   'FFEF6C00',
  bucketOk:      'FF2E7D32',
  bucketLong:    'FF1976D2',
  bucketHuge:    'FF7B1FA2',
  // Sprach-Farben
  langDE:        'FF2E7D32',
  langEN:        'FF1976D2',
  langMixed:     'FFEF6C00',
  langUnknown:   'FF9E9E9E',
};

const BUCKET_COLORS: Record<QualityBucket, string> = {
  empty: COLORS.bucketEmpty,
  stub:  COLORS.bucketStub,
  short: COLORS.bucketShort,
  ok:    COLORS.bucketOk,
  long:  COLORS.bucketLong,
  huge:  COLORS.bucketHuge,
};
const CL_BUCKET_COLORS: Record<ChecklistBucket, string> = {
  empty:       'FF9E9E9E',
  header_only: 'FFC62828',
  no_items:    'FFEF6C00',
  all_open:    'FFF57C00',
  partial:     'FF1976D2',
  all_done:    'FF2E7D32',
};
const LANG_COLORS: Record<Lang, string> = {
  de:      COLORS.langDE,
  en:      COLORS.langEN,
  mixed:   COLORS.langMixed,
  unknown: COLORS.langUnknown,
};
const LANG_LABELS: Record<Lang, string> = {
  de: 'Deutsch', en: 'Englisch', mixed: 'gemischt DE/EN', unknown: 'unbekannt',
};

function coverageColor(pct: number): string {
  if (pct >= 80) return COLORS.covHigh;
  if (pct >= 60) return COLORS.covMed;
  if (pct >= 40) return COLORS.covLow;
  return COLORS.covVeryLow;
}

function issueField(row: Record<string, unknown> | undefined, key: string): string {
  if (!row) return '';
  const v = row[key];
  if (v == null) return '';
  if (Array.isArray(v)) return v.map(String).join(', ');
  return String(v);
}

function bandRow(ws: ExcelJS.Worksheet, rowIdx: number, cols: number, bg: string, fg: string, text: string, size = 11, bold = true, height = 24) {
  ws.mergeCells(rowIdx, 1, rowIdx, cols);
  const c = ws.getCell(rowIdx, 1);
  c.value = text;
  c.font = { name: 'Calibri', size, bold, color: { argb: fg } };
  c.alignment = { vertical: 'middle', horizontal: 'left', indent: 1, wrapText: true };
  c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: bg } };
  ws.getRow(rowIdx).height = height;
}

export async function exportEBXlsx(rows: Issue[], opts: EBExportOptions): Promise<void> {
  const baseUrl = (opts.jiraBaseUrl || 'https://schueco.atlassian.net').replace(/\/+$/, '');
  const browse = (k: string) => `${baseUrl}/browse/${encodeURIComponent(k)}`;
  const stats = computeEBStats(rows);
  const coveragePct = stats.total > 0 ? (stats.withEB / stats.total) * 100 : 0;

  const wb = new ExcelJS.Workbook();
  wb.creator = 'SchueCal Dashboard';
  wb.created = new Date();

  const scopeLabel = opts.scope === 'open' ? 'nur offene Tickets'
    : opts.scope === 'closed' ? 'nur geschlossene Tickets' : 'alle Status';
  const teamLabel = opts.team || 'Alle Teams';

  // ══════════════════════════════════════════════════════════════════════
  // SHEET 1: Übersicht (KPIs)
  // ══════════════════════════════════════════════════════════════════════
  const wsOv = wb.addWorksheet('Übersicht');
  wsOv.columns = [
    { width: 30 }, { width: 20 }, { width: 20 }, { width: 30 },
  ];
  const NCOL_OV = 4;

  bandRow(wsOv, 1, NCOL_OV, COLORS.headerBg, COLORS.headerFg,
    'SchueCal · Expected Behaviour — Qualitätsübersicht', 16, true, 32);

  bandRow(wsOv, 2, NCOL_OV, COLORS.subHeaderBg, COLORS.subHeaderFg,
    'Prüft, wie oft das Custom-Feld „Expected Behaviour" bei Stories/Bugs gefüllt ist, in welcher Sprache und ob die Beschreibung ausreichend ausführlich ist. ' +
    'Ausschließlich lexikalisch — keine semantische Bewertung.', 10.5, false, 36);

  bandRow(wsOv, 3, NCOL_OV, COLORS.metaBg, COLORS.metaFg,
    `Team-Filter:  ${teamLabel}     ·     Status-Scope:  ${scopeLabel}     ·     Tickets im Scope:  ${stats.total}     ·     Erstellt:  ${new Date().toLocaleString('de-DE')}`,
    10, true, 20);

  wsOv.getRow(4).height = 6;

  // KPI-Header
  const kpiHead = wsOv.getRow(5);
  ['Kennzahl', 'Wert', 'Anteil', 'Hinweis'].forEach((t, i) => {
    const c = kpiHead.getCell(i + 1);
    c.value = t;
    c.font = { name: 'Calibri', size: 11, bold: true, color: { argb: COLORS.columnFg } };
    c.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 };
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.columnBg } };
  });
  kpiHead.height = 22;

  const kpis: Array<[string, string | number, string, string]> = [
    ['Σ Tickets im Scope',            stats.total, '',                                    'Nach Team- und Status-Filter'],
    ['mit Expected Behaviour',        stats.withEB,   `${coveragePct.toFixed(1)}%`,       'Beschreibung ist gefüllt'],
    ['ohne Expected Behaviour',       stats.withoutEB, `${(100 - coveragePct).toFixed(1)}%`, 'Feld leer'],
    ['Ø Zeichen (wenn gefüllt)',      stats.avgLen,   '',                                 'Durchschnittslänge in Zeichen'],
    ['Median Zeichen (wenn gefüllt)', stats.medianLen, '',                                'Robuster Mittelwert (weniger anfällig für Ausreißer)'],
  ];
  kpis.forEach((k, i) => {
    const r = wsOv.getRow(6 + i);
    r.getCell(1).value = k[0];
    r.getCell(2).value = k[1];
    r.getCell(3).value = k[2];
    r.getCell(4).value = k[3];
    for (let c = 1; c <= 4; c++) {
      const cell = r.getCell(c);
      cell.font = { name: 'Calibri', size: 11, bold: c <= 2 };
      cell.alignment = { vertical: 'middle', horizontal: c === 2 || c === 3 ? 'right' : 'left', indent: 1 };
      if (i % 2 === 1) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.zebraBg } };
    }
    // Coverage-Zeile farbig
    if (i === 1) {
      r.getCell(3).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: coverageColor(coveragePct) } };
      r.getCell(3).font = { name: 'Calibri', size: 11, bold: true, color: { argb: 'FFFFFFFF' } };
    }
    r.height = 22;
  });

  // Qualitäts-Buckets
  let row = 6 + kpis.length + 2;
  bandRow(wsOv, row, NCOL_OV, COLORS.columnBg, COLORS.columnFg, 'Qualitäts-Buckets (nach Textlänge)', 12, true, 22);
  row++;
  const bucketOrder: QualityBucket[] = ['empty', 'stub', 'short', 'ok', 'long', 'huge'];
  bucketOrder.forEach((b, i) => {
    const n = stats.qualityCounts[b];
    const pct = stats.total > 0 ? (n / stats.total) * 100 : 0;
    const r = wsOv.getRow(row + i);
    r.getCell(1).value = QUALITY_LABELS[b];
    r.getCell(2).value = n;
    r.getCell(3).value = `${pct.toFixed(1)}%`;
    r.getCell(4).value = bucketHint(b);
    for (let c = 1; c <= 4; c++) {
      const cell = r.getCell(c);
      cell.font = { name: 'Calibri', size: 10.5 };
      cell.alignment = { vertical: 'middle', horizontal: c === 2 || c === 3 ? 'right' : 'left', indent: 1 };
      if (i % 2 === 1) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.zebraBg } };
    }
    // Farb-Chip in Spalte 1
    r.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BUCKET_COLORS[b] } };
    r.getCell(1).font = { name: 'Calibri', size: 10.5, bold: true, color: { argb: 'FFFFFFFF' } };
    r.height = 20;
  });
  row += bucketOrder.length + 1;

  // Sprach-Verteilung (nur bei gefüllten)
  bandRow(wsOv, row, NCOL_OV, COLORS.columnBg, COLORS.columnFg, 'Sprachverteilung (nur Tickets mit Expected Behaviour)', 12, true, 22);
  row++;
  const langOrder: Lang[] = ['de', 'en', 'mixed', 'unknown'];
  langOrder.forEach((l, i) => {
    const n = stats.langCounts[l];
    const pct = stats.withEB > 0 ? (n / stats.withEB) * 100 : 0;
    const r = wsOv.getRow(row + i);
    r.getCell(1).value = LANG_LABELS[l];
    r.getCell(2).value = n;
    r.getCell(3).value = `${pct.toFixed(1)}%`;
    r.getCell(4).value = '';
    for (let c = 1; c <= 4; c++) {
      const cell = r.getCell(c);
      cell.font = { name: 'Calibri', size: 10.5 };
      cell.alignment = { vertical: 'middle', horizontal: c === 2 || c === 3 ? 'right' : 'left', indent: 1 };
      if (i % 2 === 1) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.zebraBg } };
    }
    r.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: LANG_COLORS[l] } };
    r.getCell(1).font = { name: 'Calibri', size: 10.5, bold: true, color: { argb: 'FFFFFFFF' } };
    r.height = 20;
  });

  // ══════════════════════════════════════════════════════════════════════
  // SHEET 2: Coverage pro Team + pro Type
  // ══════════════════════════════════════════════════════════════════════
  const wsCov = wb.addWorksheet('Coverage');
  wsCov.columns = [{ width: 26 }, { width: 12 }, { width: 12 }, { width: 12 }, { width: 12 }];
  const NCOL_CV = 5;

  bandRow(wsCov, 1, NCOL_CV, COLORS.headerBg, COLORS.headerFg, 'Coverage · gefüllte vs. leere Expected-Behaviour-Felder', 14, true, 28);
  bandRow(wsCov, 2, NCOL_CV, COLORS.metaBg, COLORS.metaFg,
    `Team-Filter:  ${teamLabel}     ·     Status-Scope:  ${scopeLabel}     ·     Erstellt:  ${new Date().toLocaleString('de-DE')}`,
    10, true, 20);
  wsCov.getRow(3).height = 6;

  const covHeader = (title: string, atRow: number) => {
    bandRow(wsCov, atRow, NCOL_CV, COLORS.columnBg, COLORS.columnFg, title, 12, true, 22);
    const h = wsCov.getRow(atRow + 1);
    ['Name', 'Σ', 'mit EB', 'ohne EB', 'Coverage %'].forEach((t, i) => {
      const c = h.getCell(i + 1);
      c.value = t;
      c.font = { name: 'Calibri', size: 10.5, bold: true, color: { argb: COLORS.columnFg } };
      c.alignment = { vertical: 'middle', horizontal: i === 0 ? 'left' : 'right', indent: 1 };
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF475569' } };
    });
    h.height = 20;
  };
  const renderRow = (target: number, name: string, total: number, filled: number, zebra: boolean) => {
    const pct = total > 0 ? (filled / total) * 100 : 0;
    const r = wsCov.getRow(target);
    r.getCell(1).value = name;
    r.getCell(2).value = total;
    r.getCell(3).value = filled;
    r.getCell(4).value = total - filled;
    r.getCell(5).value = pct / 100;
    r.getCell(5).numFmt = '0.0%';
    for (let c = 1; c <= 5; c++) {
      const cell = r.getCell(c);
      cell.font = { name: 'Calibri', size: 10.5 };
      cell.alignment = { vertical: 'middle', horizontal: c === 1 ? 'left' : 'right', indent: 1 };
      if (zebra) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.zebraBg } };
    }
    r.getCell(5).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: coverageColor(pct) } };
    r.getCell(5).font = { name: 'Calibri', size: 10.5, bold: true, color: { argb: 'FFFFFFFF' } };
    r.height = 20;
  };

  let rIdx = 4;
  covHeader('Coverage pro Issue-Type', rIdx);
  rIdx += 2;
  stats.perType.forEach((x, i) => { renderRow(rIdx + i, x.type, x.total, x.withEB, i % 2 === 1); });
  rIdx += stats.perType.length + 1;

  covHeader('Coverage pro Team', rIdx);
  rIdx += 2;
  stats.perTeam.forEach((x, i) => { renderRow(rIdx + i, x.team, x.total, x.withEB, i % 2 === 1); });

  wsCov.views = [{ state: 'frozen', ySplit: 3 }];

  // ══════════════════════════════════════════════════════════════════════
  // SHEET 3: Tickets (Detail-Liste)
  // ══════════════════════════════════════════════════════════════════════
  const wsT = wb.addWorksheet('Tickets');
  const T_HEAD = [
    { header: 'Key',                       width: 14, align: 'left'   as const },
    { header: 'Team',                      width: 16, align: 'left'   as const },
    { header: 'Type',                      width: 10, align: 'center' as const },
    { header: 'Status',                    width: 14, align: 'left'   as const },
    { header: 'EB Länge (Zeichen)',        width: 12, align: 'right'  as const },
    { header: 'EB Qualitäts-Bucket',       width: 22, align: 'left'   as const },
    { header: 'EB Sprache',                width: 14, align: 'left'   as const },
    { header: 'Summary',                   width: 44, align: 'left'   as const },
    { header: 'Expected Behaviour (Text)', width: 70, align: 'left'   as const },
    // ── Checkliste / AC ────────────────────────────────────────────
    { header: 'Checklist-Status',          width: 24, align: 'left'   as const },
    { header: 'AC-Header?',                width: 12, align: 'center' as const },
    { header: 'Items ✓',                   width:  9, align: 'right'  as const },
    { header: 'Items ☐',                   width:  9, align: 'right'  as const },
    { header: 'Checklist (Text)',          width: 60, align: 'left'   as const },
  ];
  const NCOL_T = T_HEAD.length;

  bandRow(wsT, 1, NCOL_T, COLORS.headerBg, COLORS.headerFg,
    'Tickets · Expected Behaviour + Checkliste — Detail pro Ticket', 14, true, 28);
  bandRow(wsT, 2, NCOL_T, COLORS.subHeaderBg, COLORS.subHeaderFg,
    'Ein Ticket pro Zeile. Zeigt Expected Behaviour UND die Checkliste (mit Acceptance-Criteria-Erkennung) im direkten Vergleich — ' +
    'so sieht man auf einen Blick, wo beides fehlt bzw. wo nur eines gepflegt ist. Sortier-Tipp: nach „EB Länge" aufsteigend.',
    10.5, false, 40);
  // Direkte Legende der farbigen Spalten (statt Sprung ins Info-Sheet)
  bandRow(wsT, 3, NCOL_T, COLORS.subHeaderBg, COLORS.subHeaderFg,
    'EB Qualitäts-Bucket: leer  ·  zu kurz (<20 Z.)  ·  kurz (20–60)  ·  gut (60–500)  ·  lang (500–2000)  ·  überlang (>2 KB).      ' +
    'EB Sprache: Deutsch / Englisch / gemischt / unbekannt (Heuristik über Marker-Wörter).      ' +
    'Checklist-Status: leer · nur Überschrift · Freitext · alle offen · teilweise · alle erledigt.',
    10, false, 40);
  bandRow(wsT, 4, NCOL_T, COLORS.metaBg, COLORS.metaFg,
    `Team-Filter:  ${teamLabel}     ·     Status-Scope:  ${scopeLabel}     ·     Σ Tickets:  ${rows.length}`,
    10, true, 20);
  wsT.getRow(5).height = 6;

  T_HEAD.forEach((h, i) => {
    const col = wsT.getColumn(i + 1);
    col.width = h.width;
    const c = wsT.getRow(6).getCell(i + 1);
    c.value = h.header;
    c.font = { name: 'Calibri', size: 10.5, bold: true, color: { argb: COLORS.columnFg } };
    c.alignment = { vertical: 'middle', horizontal: h.align, wrapText: true };
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.columnBg } };
  });
  wsT.getRow(6).height = 30;

  // Sortierung: leer & kurz zuerst → dringlichste Aufräum-Kandidaten oben
  const bucketOrderMap: Record<QualityBucket, number> = { empty: 0, stub: 1, short: 2, ok: 3, long: 4, huge: 5 };
  const sorted = [...rows].sort((a, b) => {
    const ea = expectedBehaviourText(a);
    const eb = expectedBehaviourText(b);
    const ba = bucketOrderMap[qualityBucket(ea)];
    const bb = bucketOrderMap[qualityBucket(eb)];
    if (ba !== bb) return ba - bb;
    return ea.length - eb.length;
  });

  sorted.forEach((raw, i) => {
    const r = wsT.getRow(7 + i);
    const rec = raw as unknown as Record<string, unknown>;
    const eb = expectedBehaviourText(raw);
    const bucket = qualityBucket(eb);
    const lang = eb ? detectLang(eb) : 'unknown';
    const key = issueField(rec, 'Issue key');
    // Checklist parsen
    const clText = checklistText(raw);
    const cl = parseChecklist(clText);
    const clBucket = checklistBucket(cl);

    r.getCell(1).value  = key ? { text: key, hyperlink: browse(key), tooltip: 'In JIRA öffnen' } : '';
    r.getCell(2).value  = issueField(rec, 'Team Name');
    r.getCell(3).value  = issueField(rec, 'Issue Type');
    r.getCell(4).value  = issueField(rec, 'Status');
    r.getCell(5).value  = eb.length;
    r.getCell(6).value  = QUALITY_LABELS[bucket];
    r.getCell(7).value  = LANG_LABELS[lang];
    r.getCell(8).value  = issueField(rec, 'Summary');
    r.getCell(9).value  = eb;
    r.getCell(10).value = CHECKLIST_LABELS[clBucket];
    r.getCell(11).value = cl.hasACHeader ? 'ja' : '';
    r.getCell(12).value = cl.itemsDone;
    r.getCell(13).value = cl.itemsOpen;
    r.getCell(14).value = clText;

    const zebra = i % 2 === 1;
    for (let c = 1; c <= NCOL_T; c++) {
      const cell = r.getCell(c);
      cell.font = { name: 'Calibri', size: 10 };
      cell.alignment = {
        vertical: 'top',
        horizontal: T_HEAD[c - 1].align,
        wrapText: c === 8 || c === 9 || c === 14,
      };
      cell.border = { bottom: { style: 'hair', color: { argb: COLORS.border } } };
      if (zebra) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.zebraBg } };
    }
    // Key-Zelle Link-Style
    r.getCell(1).font = { name: 'Calibri', size: 10, bold: true, color: { argb: COLORS.linkFg }, underline: true };
    // EB Bucket farbig
    r.getCell(6).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: BUCKET_COLORS[bucket] } };
    r.getCell(6).font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
    // Sprache farbig
    r.getCell(7).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: LANG_COLORS[lang] } };
    r.getCell(7).font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
    // Checklist-Status farbig
    r.getCell(10).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: CL_BUCKET_COLORS[clBucket] } };
    r.getCell(10).font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
    // AC-Header ja: grün
    if (cl.hasACHeader) {
      r.getCell(11).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.covHigh } };
      r.getCell(11).font = { name: 'Calibri', size: 10, bold: true, color: { argb: 'FFFFFFFF' } };
    }
    r.height = 28;
  });

  wsT.views = [{ state: 'frozen', ySplit: 6, xSplit: 1 }];
  wsT.autoFilter = {
    from: { row: 6, column: 1 },
    to:   { row: 6 + sorted.length, column: NCOL_T },
  };

  // ══════════════════════════════════════════════════════════════════════
  // SHEET 4: Legende & Info
  // ══════════════════════════════════════════════════════════════════════
  const info = wb.addWorksheet('Legende & Info');
  info.columns = [{ width: 24 }, { width: 90 }];
  info.mergeCells(1, 1, 1, 2);
  info.getCell(1, 1).value = 'SchueCal · Expected Behaviour — Legende';
  info.getCell(1, 1).font = { name: 'Calibri', size: 16, bold: true, color: { argb: COLORS.headerFg } };
  info.getCell(1, 1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.headerBg } };
  info.getCell(1, 1).alignment = { vertical: 'middle', indent: 1 };
  info.getRow(1).height = 30;

  const legend: Array<[string, string]> = [
    ['Feld / Bucket', 'Bedeutung'],
    ['Expected Behaviour', 'JIRA-Custom-Feld „Custom field (Expected Behaviour)" — was das Ticket eigentlich tun sollte / erwartet wird.'],
    ['EB Qualitäts-Bucket', 'Grobe Qualitätsstufe der Beschreibung nach Zeichenzahl. Steuert die Farbe der Bucket-Zelle im Tickets-Sheet.'],
    ['   · leer',           'Feld ist nicht gepflegt.'],
    [`   · ${QUALITY_LABELS.stub}`,  '< 20 Zeichen — meist Stichwort oder „s.o.", faktisch unbrauchbar.'],
    [`   · ${QUALITY_LABELS.short}`, '20–60 Zeichen — Ein-Satz-Beschreibung, oft zu knapp.'],
    [`   · ${QUALITY_LABELS.ok}`,    '60–500 Zeichen — vernünftig ausformuliert.'],
    [`   · ${QUALITY_LABELS.long}`,  '500–2000 Zeichen — sehr ausführlich (meist OK).'],
    [`   · ${QUALITY_LABELS.huge}`,  '> 2000 Zeichen — Copy-Paste-verdächtig oder ausufernd.'],
    ['',                    ''],
    ['EB Sprache',          'Automatische Sprach-Erkennung des Beschreibungstextes. Heuristik über Marker-Wörter (der/die/das vs. the/and/is …). '
                            + 'Werte: Deutsch, Englisch, gemischt DE/EN (beides prominent), unbekannt (kein Signal — meist zu kurz oder nur Zahlen/Codes).'],
    ['',                    ''],
    ['Coverage-Ampel',      '≥ 80% grün · 60–79% gelb · 40–59% orange · < 40% rot'],
    ['',                    ''],
    ['Checklist-Status',    'Zustand der JIRA-Checkliste („Custom field (Checklist Text)"), Werte: leer · nur Überschrift ohne Items · Freitext ohne Items · alle Items offen · teilweise erledigt · alle Items erledigt.'],
    ['AC-Header?',          'Kennzeichnet „ja", wenn die Checkliste eine Sektion enthält, die „Acceptance Criteria" / „Akzeptanzkriterien" / „Abnahmekriterien" / „AC" heißt.'],
    ['Items ✓ / ☐',         'Anzahl erledigter („[x]") bzw. offener („[]") Checkbox-Items in der Checkliste des Tickets.'],
    ['',                    ''],
    ['Was NICHT geprüft wird', 'Semantische Qualität (macht der Text überhaupt Sinn?). Der Export prüft nur Vorhandensein, Textlänge, Sprache und die Checklist-Struktur.'],
    ['',                    ''],
    ['Vorgehen',
                            '1) Sortiere Sheet „Tickets" nach „EB Länge" aufsteigend — leere/kurze Tickets stehen oben.  '
                            + '2) Kombination mit Checklist-Status: Zeilen wo BEIDES leer/rot ist, sind die dringendsten Aufräum-Kandidaten.  '
                            + '3) Team + Type bilden Prioritäts-Cluster.  '
                            + '4) Fortschritt wird beim nächsten Export sofort sichtbar (Coverage-Ampel).'],
  ];
  legend.forEach((l, i) => {
    const r = info.getRow(2 + i);
    r.getCell(1).value = l[0];
    r.getCell(2).value = l[1];
    r.getCell(1).font = { name: 'Calibri', size: 11, bold: i === 0 };
    r.getCell(2).font = { name: 'Calibri', size: 10.5 };
    r.getCell(1).alignment = { vertical: 'top' };
    r.getCell(2).alignment = { vertical: 'top', wrapText: true };
    r.height = l[1].length > 100 ? 48 : 20;
    if (i === 0) {
      for (let c = 1; c <= 2; c++) {
        r.getCell(c).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: COLORS.columnBg } };
        r.getCell(c).font = { name: 'Calibri', size: 11, bold: true, color: { argb: COLORS.columnFg } };
      }
    }
  });

  // ─── Datei erzeugen & Download auslösen ──────────────────────────────────
  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const stamp = new Date().toISOString().slice(0, 10);
  const slug = (opts.team || 'alle-teams').replace(/[^\w-]+/g, '-').toLowerCase();
  const filename = `schuecal-expected-behaviour_${slug}_${opts.scope}_${stamp}.xlsx`;
  const a = document.createElement('a');
  a.href = URL.createObjectURL(blob);
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(a.href), 5000);
}

function bucketHint(b: QualityBucket): string {
  switch (b) {
    case 'empty': return 'ohne Beschreibung — höchste Priorität zum Nachpflegen';
    case 'stub':  return '„s.o.", einzelne Wörter — faktisch nicht nutzbar';
    case 'short': return 'ein Satz — oft zu wenig Kontext';
    case 'ok':    return 'vernünftig ausformuliert';
    case 'long':  return 'sehr ausführlich';
    case 'huge':  return 'Copy-Paste-verdächtig / ausufernd';
  }
}
